import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api } from '../hooks/useAuth';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';

const InvoiceEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState(1);

  // Fetch invoice data
  const { data: invoice, isLoading: invoiceLoading } = useQuery({
    queryKey: ['invoice', id],
    queryFn: async () => {
      const response = await api.get(`/invoices/${id}`);
      return response.data;
    },
  });

  // Fetch companies
  const { data: companies } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await api.get('/companies');
      return response.data;
    },
  });

  // Fetch customers
  const { data: customers } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const response = await api.get('/customers');
      return response.data;
    },
  });

  // Fetch settings
  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await api.get('/settings');
      return response.data;
    },
  });

  // Validation schema
  const invoiceItemSchema = z.object({
    description: z.string().min(1, 'validation.required'),
    qty: z.number().min(0.01, 'validation.required'),
    unitPrice: z.number().min(0, 'validation.required'),
    vatRate: z.number().min(0).max(100),
  });

  const invoiceSchema = z.object({
    companyId: z.string().min(1, 'validation.required'),
    customerId: z.string().min(1, 'validation.required'),
    invoiceDate: z.string().min(1, 'validation.required'),
    invoiceNumber: z.string().min(1, 'validation.required'),
    currency: z.enum(['EUR', 'USD', 'TRY', 'RUB']),
    dateType: z.enum(['zeitraum', 'lieferdatum']).optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    deliveryDate: z.string().optional(),
    purpose: z.string().optional(),
    status: z.enum(['draft', 'sent', 'paid', 'unpaid', 'void']).optional(),
    paymentMethod: z.enum(['bank', 'credit_card', 'invoice']).optional(),
    paidAmount: z.number().min(0).optional(),
    items: z.array(invoiceItemSchema).min(1, 'validation.required'),
    notes: z.string().optional(),
  }).refine((data) => {
    if (data.dateType === 'zeitraum') {
      return data.startDate && data.endDate;
    }
    if (data.dateType === 'lieferdatum') {
      return data.deliveryDate;
    }
    return true;
  }, {
    message: 'validation.dateFieldsRequired',
    path: ['dateType']
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(invoiceSchema),
    defaultValues: {
      invoiceDate: dayjs().format('YYYY-MM-DD'),
      invoiceNumber: `0001-${dayjs().year()}`,
      currency: settings?.defaultCurrency || 'EUR',
      dateType: 'lieferdatum',
      status: 'draft',
      paymentMethod: 'invoice',
      paidAmount: 0,
      notes: 'Die Rechnung ist innerhalb von 7 Tagen nach Erhalt zahlbar',
      items: [
        {
          description: '',
          qty: 1,
          unitPrice: 0,
          vatRate: settings?.taxDefaults?.vatRate || 19,
        }
      ],
    },
  });

  // Set form values when invoice data is loaded
  useEffect(() => {
    if (invoice) {
      setValue('companyId', invoice.companyId._id);
      setValue('customerId', invoice.customerId._id);
      setValue('invoiceDate', dayjs(invoice.invoiceDate).format('YYYY-MM-DD'));
      setValue('invoiceNumber', invoice.invoiceNumber);
      setValue('currency', invoice.currency);
      setValue('dateType', invoice.dateType || 'lieferdatum');
      setValue('startDate', invoice.startDate ? dayjs(invoice.startDate).format('YYYY-MM-DD') : '');
      setValue('endDate', invoice.endDate ? dayjs(invoice.endDate).format('YYYY-MM-DD') : '');
      setValue('deliveryDate', invoice.deliveryDate ? dayjs(invoice.deliveryDate).format('YYYY-MM-DD') : '');
      setValue('purpose', invoice.purpose || '');
      setValue('status', invoice.status);
      setValue('paymentMethod', invoice.paymentMethod || 'invoice');
      setValue('paidAmount', invoice.paidAmount || 0);
      setValue('notes', invoice.notes || '');
      setValue('items', invoice.items || []);
    }
  }, [invoice, setValue]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.put(`/invoices/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      Swal.fire({
        icon: 'success',
        title: t('common.success'),
        text: t('invoices.updatedSuccess'),
        timer: 2000,
        showConfirmButton: false
      }).then(() => {
        navigate('/invoices');
      });
    },
    onError: (error) => {
      console.error('Invoice update error:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.message || t('invoices.updateFailed');
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: errorMessage,
        confirmButtonText: t('common.ok')
      });
    },
  });

  const watchedItems = watch('items') || [];
  const watchedDateType = watch('dateType');
  const watchedStatus = watch('status');

  // Calculate totals
  const totals = watchedItems.reduce(
    (acc, item) => {
      const subtotal = item.qty * item.unitPrice;
      const vatAmount = subtotal * (item.vatRate / 100);
      acc.subtotal += subtotal;
      acc.vatTotal += vatAmount;
      acc.grandTotal += subtotal + vatAmount;
      return acc;
    },
    { subtotal: 0, vatTotal: 0, grandTotal: 0 }
  );

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const onSubmit = (data) => {
    console.log('Invoice update form submitted:', data);
    updateMutation.mutate(data);
  };

  const onError = (errors) => {
    console.log('Invoice update form validation errors:', errors);
    
    // Get first error and show alert
    const firstErrorPath = Object.keys(errors)[0];
    const firstError = errors[firstErrorPath];
    
    if (firstError) {
      const errorMessage = firstError.message === 'validation.dateFieldsRequired' ? 
        t('validation.dateFieldsRequired') : 
        (firstError.message || 'Validation error');
      
      Swal.fire({
        icon: 'error',
        title: t('common.validationError'),
        text: `${firstErrorPath && `Field "${firstErrorPath}": `}${errorMessage}`,
        confirmButtonText: t('common.ok')
      });
    }
  };

  const addItem = () => {
    const currentItems = watch('items') || [];
    setValue('items', [
      ...currentItems,
      {
        description: '',
        qty: 1,
        unitPrice: 0,
        vatRate: settings?.taxDefaults?.vatRate || 19,
      }
    ]);
  };

  const removeItem = (index) => {
    const currentItems = watch('items') || [];
    if (currentItems.length > 1) {
      setValue('items', currentItems.filter((_, i) => i !== index));
    }
  };

  if (invoiceLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900">{t('invoices.notFound')}</h2>
        <p className="text-gray-500 mt-2">{t('invoices.invoiceNotExist')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('invoices.editInvoice')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('invoices.updateInvoiceDesc')}
          </p>
        </div>
        <button
          onClick={() => navigate('/invoices')}
          className="btn btn-secondary"
        >
          {t('common.backToInvoices')}
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit, onError)} className="space-y-6">
        {/* Step 1: Company & Customer */}
        {currentStep === 1 && (
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">{t('invoices.companyCustomer')}</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="label">{t('invoices.company')}</label>
                <select
                  {...register('companyId')}
                  className={`input ${errors.companyId ? 'input-error' : ''}`}
                >
                  <option value="">{t('invoices.selectCompany')}</option>
                  {companies?.map((company) => (
                    <option key={company._id} value={company._id}>
                      {company.name}
                    </option>
                  ))}
                </select>
                {errors.companyId && (
                  <p className="error-message">{t(errors.companyId.message)}</p>
                )}
              </div>
              <div>
                <label className="label">{t('invoices.customer')}</label>
                <select
                  {...register('customerId')}
                  className={`input ${errors.customerId ? 'input-error' : ''}`}
                >
                  <option value="">{t('invoices.selectCustomer')}</option>
                  {customers?.map((customer) => (
                    <option key={customer._id} value={customer._id}>
                      {customer.name}
                    </option>
                  ))}
                </select>
                {errors.customerId && (
                  <p className="error-message">{t(errors.customerId.message)}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Invoice Details */}
        {currentStep === 2 && (
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">{t('invoices.invoiceDetails')}</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="label">{t('invoices.invoiceDate')}</label>
                <input
                  {...register('invoiceDate')}
                  type="date"
                  className={`input ${errors.invoiceDate ? 'input-error' : ''}`}
                />
                {errors.invoiceDate && (
                  <p className="error-message">{t(errors.invoiceDate.message)}</p>
                )}
              </div>
              <div>
                <label className="label">{t('invoices.invoiceNumber')}</label>
                <input
                  {...register('invoiceNumber')}
                  type="text"
                  className={`input ${errors.invoiceNumber ? 'input-error' : ''}`}
                  placeholder={t('invoices.invoiceNumberPlaceholder')}
                />
                {errors.invoiceNumber && (
                  <p className="error-message">{t(errors.invoiceNumber.message)}</p>
                )}
              </div>
            </div>
            {/* Date Selection Type */}
            <div className="mt-4">
              <label className="label">{t('invoices.dateSelectionType')}</label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    {...register('dateType')}
                    type="radio"
                    value="zeitraum"
                    className="mr-2"
                  />
                  {t('invoices.zeitraum')}
                </label>
                <label className="flex items-center">
                  <input
                    {...register('dateType')}
                    type="radio"
                    value="lieferdatum"
                    className="mr-2"
                  />
                  {t('invoices.lieferdatum')}
                </label>
              </div>
            </div>
            {/* Conditional Date Fields */}
            {watchedDateType === 'zeitraum' && (
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="label">{t('invoices.startDate')}</label>
                  <input
                    {...register('startDate')}
                    type="date"
                    className={`input ${errors.startDate ? 'input-error' : ''}`}
                  />
                  {errors.startDate && (
                    <p className="error-message">{t(errors.startDate.message)}</p>
                  )}
                </div>
                <div>
                  <label className="label">{t('invoices.endDate')}</label>
                  <input
                    {...register('endDate')}
                    type="date"
                    className={`input ${errors.endDate ? 'input-error' : ''}`}
                  />
                  {errors.endDate && (
                    <p className="error-message">{t(errors.endDate.message)}</p>
                  )}
                </div>
              </div>
            )}
            {watchedDateType === 'lieferdatum' && (
              <div className="mt-4">
                <div>
                  <label className="label">{t('invoices.deliveryDate')}</label>
                  <input
                    {...register('deliveryDate')}
                    type="date"
                    className={`input ${errors.deliveryDate ? 'input-error' : ''}`}
                  />
                  {errors.deliveryDate && (
                    <p className="error-message">{t(errors.deliveryDate.message)}</p>
                  )}
                </div>
              </div>
            )}
            {/* Purpose */}
            <div className="mt-4">
              <label className="label">{t('invoices.purpose')}</label>
              <input
                {...register('purpose')}
                type="text"
                className="input"
                placeholder={t('invoices.purposePlaceholder')}
              />
            </div>
            <div className="mt-4">
              <label className="label">{t('common.currency')}</label>
              <select {...register('currency')} className="input">
                <option value="EUR">EUR - Euro</option>
                <option value="USD">USD - US Dollar</option>
                <option value="TRY">TRY - Turkish Lira</option>
                <option value="RUB">RUB - Russian Ruble</option>
              </select>
            </div>
          </div>
        )}

        {/* Step 3: Items */}
        {currentStep === 3 && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">{t('invoices.items')}</h2>
              <button
                type="button"
                onClick={addItem}
                className="btn btn-primary btn-sm"
              >
                {t('invoices.addItem')}
              </button>
            </div>
            <div className="space-y-4">
              {watchedItems.map((item, index) => (
                <div key={index} className="grid grid-cols-1 gap-4 sm:grid-cols-5 p-4 border rounded-lg">
                  <div className="sm:col-span-2">
                    <label className="label">{t('invoices.description')}</label>
                    <input
                      {...register(`items.${index}.description`)}
                      className={`input ${errors.items?.[index]?.description ? 'input-error' : ''}`}
                      placeholder={t('invoices.description')}
                    />
                    {errors.items?.[index]?.description && (
                      <p className="error-message">{t(errors.items[index].description.message)}</p>
                    )}
                  </div>
                  <div>
                    <label className="label">{t('invoices.quantity')}</label>
                    <input
                      {...register(`items.${index}.qty`, { valueAsNumber: true })}
                      type="number"
                      step="0.01"
                      min="0"
                      className={`input ${errors.items?.[index]?.qty ? 'input-error' : ''}`}
                    />
                    {errors.items?.[index]?.qty && (
                      <p className="error-message">{t(errors.items[index].qty.message)}</p>
                    )}
                  </div>
                  <div>
                    <label className="label">{t('invoices.unitPrice')}</label>
                    <input
                      {...register(`items.${index}.unitPrice`, { valueAsNumber: true })}
                      type="number"
                      step="0.01"
                      min="0"
                      className={`input ${errors.items?.[index]?.unitPrice ? 'input-error' : ''}`}
                    />
                    {errors.items?.[index]?.unitPrice && (
                      <p className="error-message">{t(errors.items[index].unitPrice.message)}</p>
                    )}
                  </div>
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      disabled={watchedItems.length === 1}
                      className="btn btn-danger btn-sm w-full"
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 4: Review */}
        {currentStep === 4 && (
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">{t('invoices.reviewInvoice')}</h2>
            <div className="space-y-4">
              <div>
                <label className="label">{t('invoices.notes')}</label>
                <textarea
                  {...register('notes')}
                  className="input"
                  rows={3}
                  placeholder={t('invoices.notes')}
                />
              </div>

              {/* Payment Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">{t('invoices.paymentInfo')}</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="label">{t('invoices.status')}</label>
                    <select {...register('status')} className="input">
                      <option value="draft">{t('invoices.statuses.draft')}</option>
                      <option value="sent">{t('invoices.statuses.sent')}</option>
                      <option value="paid">{t('invoices.statuses.paid')}</option>
                      <option value="unpaid">{t('invoices.statuses.unpaid')}</option>
                      <option value="void">{t('invoices.statuses.void')}</option>
                    </select>
                  </div>
                  {(watchedStatus === 'paid' || watchedStatus === 'unpaid') && (
                    <div>
                      <label className="label">{t('invoices.paymentMethod')}</label>
                      <select {...register('paymentMethod')} className="input">
                        <option value="bank">{t('invoices.paymentMethods.bank')}</option>
                        <option value="credit_card">{t('invoices.paymentMethods.credit_card')}</option>
                        <option value="invoice">{t('invoices.paymentMethods.invoice')}</option>
                      </select>
                    </div>
                  )}
                </div>
                
                {watchedStatus === 'unpaid' && (
                  <div className="mt-4">
                    <label className="label">{t('invoices.paidAmount')}</label>
                    <input
                      {...register('paidAmount', { valueAsNumber: true })}
                      type="number"
                      min="0"
                      step="0.01"
                      className="input"
                      placeholder="0.00"
                    />
                  </div>
                )}
              </div>
              
              {/* Totals */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('common.totals')}</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>{t('common.subtotal')}:</span>
                    <span>{formatCurrency(totals.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('common.vat')}:</span>
                    <span>{formatCurrency(totals.vatTotal)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span>{t('common.total')}:</span>
                    <span>{formatCurrency(totals.grandTotal)}</span>
                  </div>
                  {watchedStatus === 'unpaid' && (
                    <div className="flex justify-between text-sm text-gray-600">
                        <span>{t('invoices.paid')}</span>
                      <span>{formatCurrency(watch('paidAmount') || 0)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </form>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <div>
          {currentStep > 1 && (
            <button
              type="button"
              onClick={() => setCurrentStep(currentStep - 1)}
              className="btn btn-secondary"
            >
              {t('common.previous')}
            </button>
          )}
        </div>
        <div>
          {currentStep < 4 ? (
            <button
              type="button"
              onClick={() => setCurrentStep(currentStep + 1)}
              className="btn btn-primary"
            >
              {t('common.next')}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                // Trigger form submission manually
                const form = document.querySelector('form');
                if (form) {
                  form.requestSubmit();
                }
              }}
              disabled={updateMutation.isPending}
              className="btn btn-primary"
            >
              {updateMutation.isPending ? t('common.saving') : t('invoices.updateInvoice')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceEdit;
